import { useEffect } from 'react';

/**
 * One-shot welcome jingle. Mounted only on the RSVP route so /upload stays
 * silent. Renders no DOM — the <audio> is created in JS so unmounting the
 * component fully tears it down and there's nothing for /upload to inherit.
 *
 * Restart-on-return: every mount constructs a fresh Audio object and calls
 * tryPlay() from a fresh closure, so navigating /upload → /<id> gets the
 * jingle again from 0:00 (not a resume of the paused instance). The unmount
 * cleanup pauses + drops the previous src so nothing leaks between mounts.
 *
 * Browsers block audio autoplay until a user gesture, so we attempt play()
 * immediately, on `canplaythrough`, and on every gesture type Chromium honors
 * as a user activation. mouseover/pointerover are included as a no-cost
 * best-effort for repeat visitors with this origin in their Media Engagement
 * Index. Once playback begins, listeners detach so it can't play twice in a
 * single mount — but the next mount is unaffected.
 */
export default function WelcomeAudio() {
  useEffect(() => {
    const audio = new Audio('/assets/welcome.mp3');
    audio.preload = 'auto';
    audio.loop = false;
    audio.currentTime = 0;
    let played = false;

    const tryPlay = () => {
      if (played) return;
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { played = true; }).catch(() => {});
      }
    };

    const onPlaying = () => { played = true; };
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('canplaythrough', tryPlay, { once: true });
    tryPlay();

    const gestureEvents = [
      'pointerdown', 'pointerup', 'click', 'mousedown', 'mouseup',
      'touchstart', 'touchend', 'keydown', 'keyup',
      'mouseover', 'pointerover', 'scroll',
    ];
    const onGesture = () => {
      tryPlay();
      if (played) detachGestureListeners();
    };
    const detachGestureListeners = () => {
      gestureEvents.forEach((ev) => {
        window.removeEventListener(ev, onGesture, true);
      });
    };
    gestureEvents.forEach((ev) => {
      window.addEventListener(ev, onGesture, { capture: true, passive: true });
    });

    return () => {
      detachGestureListeners();
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('canplaythrough', tryPlay);
      audio.pause();
      audio.src = '';
    };
  }, []);

  return null;
}
